import { useRef } from 'react';
import {
  Image,
  Type,
  Undo2,
  Redo2,
  Trash2,
  Copy,
  FlipHorizontal,
  FlipVertical,
  AlignCenterHorizontal,
  AlignCenterVertical,
  ChevronsUp,
  ChevronsDown,
  Layers,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  useCanvasStateManager,
  useCanUndo,
  useCanRedo,
} from '@/stores/canvasStateManager';
import {
  useImageOperations,
  useTextOperations,
  useObjectOperations,
} from '@/hooks';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import { useUIStore } from '@/stores';

/**
 * Toolbar button with tooltip
 */
interface ToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  loading?: boolean;
}

function ToolbarButton({
  icon,
  label,
  shortcut,
  onClick,
  disabled,
  active,
  loading,
}: ToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={active ? 'secondary' : 'ghost'}
          size="icon"
          onClick={onClick}
          disabled={disabled || loading}
          aria-label={shortcut ? `${label} (${shortcut})` : label}
          aria-pressed={active}
          className={cn(
            'h-10 w-10 rounded-control',
            active && 'bg-primary-50 text-primary',
            loading && 'animate-pulse'
          )}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">
        <p>
          {label}
          {shortcut && (
            <span className="ml-2 text-[var(--mute)]">{shortcut}</span>
          )}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Separator line between tool groups
 */
function ToolbarSeparator() {
  return <div className="my-2 h-px w-full bg-[var(--line)]" />;
}

/**
 * Props for LeftToolbar
 */
interface LeftToolbarProps {
  onLayersPanelToggle?: () => void;
  isLayersPanelOpen?: boolean;
}

/**
 * Left Toolbar component - contains design tools
 */
export function LeftToolbar({
  onLayersPanelToggle,
  isLayersPanelOpen,
}: LeftToolbarProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wasFullscreenRef = useRef(false);

  const { undo, redo } = useCanvasStateManager();

  // Use reactive selectors for undo/redo state
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  // Hooks
  const { handleFileInput, isLoading: isImageLoading } = useImageOperations();
  const { addText } = useTextOperations();

  const isEmbroideryProduct = useEmbroideryStore((s) => s.isEmbroideryProduct);

  // Text never belongs on an embroidery product: its colours never reach the
  // palette, the counter or the mapping, so they would ship past the thread
  // ceiling unseen. Locking on isEmbroideryActive alone left that open for as
  // long as no motif had been uploaded yet.
  const textLocked = isEmbroideryProduct;
  // Images too: this button feeds the plain-image path, which sets no motif
  // marker and never reaches the palette — so an image added here before the
  // first upload was invisible to every thread check and published unmapped.
  // Embroidery artwork goes in through the canvas drop zone, which routes it
  // into the analysis.
  const imageLocked = isEmbroideryProduct;
  const isFullscreen = useUIStore((s) => s.isFullscreenEditor);
  const setFullscreen = useUIStore((s) => s.setFullscreenEditor);

  const {
    hasSelection,
    deleteSelected,
    duplicateSelected,
    bringToFront,
    sendToBack,
    flipHorizontal,
    flipVertical,
    centerHorizontal,
    centerVertical,
  } = useObjectOperations();

  // Tool handlers
  const handleAddImage = () => {
    // Browser exits fullscreen when file dialog opens — capture state to restore after
    wasFullscreenRef.current = !!document.fullscreenElement;
    fileInputRef.current?.click();
  };

  const handleFileInputWithFullscreenRestore = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    await handleFileInput(e);
    if (wasFullscreenRef.current && !document.fullscreenElement) {
      wasFullscreenRef.current = false;
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  const handleAddText = () => {
    addText();
  };

  const handleUndo = async () => {
    if (canUndo) {
      await undo();
    }
  };

  const handleRedo = async () => {
    if (canRedo) {
      await redo();
    }
  };

  return (
    <aside className="flex w-14 flex-col items-center border-r border-[var(--line)] bg-[var(--paper)] py-4">
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputWithFullscreenRestore}
        className="hidden"
      />

      {/* Add Tools */}
      <div className="flex flex-col items-center gap-1">
        <ToolbarButton
          icon={<Image className="h-5 w-5" />}
          label={
            imageLocked
              ? t('creator.toolbar.addImageLocked')
              : t('creator.toolbar.addImage')
          }
          shortcut="I"
          onClick={handleAddImage}
          loading={isImageLoading}
          disabled={imageLocked}
        />
        <ToolbarButton
          icon={<Type className="h-5 w-5" />}
          label={
            textLocked
              ? t('creator.toolbar.addTextLocked')
              : t('creator.toolbar.addText')
          }
          shortcut="T"
          onClick={handleAddText}
          disabled={textLocked}
        />
      </div>

      <ToolbarSeparator />

      {/* Edit Tools */}
      <div className="flex flex-col items-center gap-1">
        <ToolbarButton
          icon={<Undo2 className="h-5 w-5" />}
          label={t('creator.toolbar.undo')}
          shortcut="Ctrl+Z"
          onClick={handleUndo}
          disabled={!canUndo}
        />
        <ToolbarButton
          icon={<Redo2 className="h-5 w-5" />}
          label={t('creator.toolbar.redo')}
          shortcut="Ctrl+Y"
          onClick={handleRedo}
          disabled={!canRedo}
        />
      </div>

      <ToolbarSeparator />

      {/* Object Tools */}
      <div className="flex flex-col items-center gap-1">
        <ToolbarButton
          icon={<Copy className="h-5 w-5" />}
          label={t('creator.toolbar.duplicate')}
          shortcut="Ctrl+D"
          onClick={duplicateSelected}
          disabled={!hasSelection}
        />
        <ToolbarButton
          icon={<Trash2 className="h-5 w-5" />}
          label={t('common.delete')}
          shortcut="Del"
          onClick={deleteSelected}
          disabled={!hasSelection}
        />
      </div>

      <ToolbarSeparator />

      {/* Layer Tools */}
      <div className="flex flex-col items-center gap-1">
        <ToolbarButton
          icon={<ChevronsUp className="h-5 w-5" />}
          label={t('creator.toolbar.bringToFront')}
          onClick={bringToFront}
          disabled={!hasSelection}
        />
        <ToolbarButton
          icon={<ChevronsDown className="h-5 w-5" />}
          label={t('creator.toolbar.sendToBack')}
          onClick={sendToBack}
          disabled={!hasSelection}
        />
        <div data-layers-button>
          <ToolbarButton
            icon={<Layers className="h-5 w-5" />}
            label={t('creator.toolbar.layers')}
            onClick={onLayersPanelToggle}
            active={isLayersPanelOpen}
          />
        </div>
      </div>

      <ToolbarSeparator />

      {/* Transform Tools */}
      <div className="flex flex-col items-center gap-1">
        <ToolbarButton
          icon={<FlipHorizontal className="h-5 w-5" />}
          label={t('creator.toolbar.flipHorizontal')}
          onClick={flipHorizontal}
          disabled={!hasSelection}
        />
        <ToolbarButton
          icon={<FlipVertical className="h-5 w-5" />}
          label={t('creator.toolbar.flipVertical')}
          onClick={flipVertical}
          disabled={!hasSelection}
        />
        <ToolbarButton
          icon={<AlignCenterHorizontal className="h-5 w-5" />}
          label={t('creator.toolbar.centerVertical')}
          onClick={centerVertical}
          disabled={!hasSelection}
        />
        <ToolbarButton
          icon={<AlignCenterVertical className="h-5 w-5" />}
          label={t('creator.toolbar.centerHorizontal')}
          onClick={centerHorizontal}
          disabled={!hasSelection}
        />
      </div>

      <ToolbarSeparator />

      {/* Fullscreen toggle */}
      <ToolbarButton
        icon={
          isFullscreen ? (
            <Minimize2 className="h-5 w-5" />
          ) : (
            <Maximize2 className="h-5 w-5" />
          )
        }
        label={
          isFullscreen
            ? t('creator.toolbar.exitFullscreen')
            : t('creator.toolbar.fullscreen')
        }
        shortcut="Esc"
        onClick={() => setFullscreen(!isFullscreen)}
      />
    </aside>
  );
}
